import { useMemo } from 'react';
import * as yup from 'yup';
import { IDetailsFormData } from '../types/IDetailsFormData';
import { useSignUpSchema } from '../../../../../auth/sign-up/ui/form/helpers/useSignUpSchema';
import { UNITED_STATES } from '../../../../domain/united-states/domain/constants/UnitedStates';
import { useTranslation } from '../../../../../shared/i18n/translate/useTranslation';
import { isValidPhoneNumber } from '../helpers/isValidPhoneNumber';
import { ReasonForTravel } from '../../../../domain/booking/models/ITravelDetails';

interface IOptions {
	requiresConsent?: boolean;
}

export function useDetailsSchema({ requiresConsent = true }: IOptions = {}) {
	const { fields: signUpSchema } = useSignUpSchema();
	const { t } = useTranslation();

	const schema = useMemo(
		() =>
			yup
				.object<IDetailsFormData>({
					firstName: signUpSchema.firstName,
					lastName: signUpSchema.lastName,
					email: signUpSchema.email,
					phoneNumber: yup
						.string()
						.required(t('phone_number_form_field_error'))
						.test(
							'is-valid-phone-number',
							withMessageWhenNotEmpty(
								t('parking_personal_details_phone_number_format_error'),
							),
							isValidPhoneNumber,
						),
					address: yup.string().required(t('address_form_field_error')),
					city: yup.string().required(t('city_form_field_error')),
					state: yup
						.string()
						.oneOf(UNITED_STATES.map((item) => item.name))
						.required(t('state_form_field_error')),
					zipCode: yup.string().required(t('zip_code_form_field_error')),
					licensePlateNumber: yup
						.string()
						.min(1, t('user_form_license_plate_invalid_error'))
						.required(t('license_plate_form_field_error')),
					confirmLicensePlateNumber: yup
						.string()
						.oneOf<string>(
							[yup.ref('licensePlateNumber'), ''],
							t('user_form_confirm_license_plate_number_mismatch_error'),
						)
						.required(t('license_plate_form_field_error')),
					saveVehicle: yup.boolean().required().default(false),
					shouldCreateNewUserAccount: yup.boolean(),
					password: yup.string().when('shouldCreateNewUserAccount', {
						is: true,
						then: signUpSchema.password,
						otherwise: yup.string().strip(true),
					}),
					confirmPassword: yup.string().when('shouldCreateNewUserAccount', {
						is: true,
						then: signUpSchema.confirmPassword,
						otherwise: yup.string().strip(true),
					}),
					usedExistingVehicleFromAccount: yup
						.boolean()
						.default(false)
						.required()
						.defined(),
					termsAndConditions: requiresConsent
						? signUpSchema.termsAndConditions
						: yup.boolean().default(false).defined(),
					reasonForTravel: yup
						.string()
						.oneOf(Object.values(ReasonForTravel))
						.required(),
				})
				.required(),
		[signUpSchema, requiresConsent],
	);

	return schema;
}

function withMessageWhenNotEmpty(message: string) {
	return ({ originalValue }: { originalValue?: string }) =>
		// When no value was entered we don't provide an explicit error message
		originalValue ? message : '';
}
